/**
 * Nova's four-phase journey spine and the canonical mappings from a
 * deliverable/queue artifact to the phase ("room") it belongs to. This is the
 * single source of truth consumed by both the presentation layer
 * (`~/components/nova/personas` re-exports these) and server code (e.g. the
 * create_deliverable hand-off message). Kept dependency-free so it is safe to
 * import anywhere — the presentation registry in `personas.ts` carries the
 * icon/avatar imports that must NOT leak into the server bundle.
 *
 * Aliases: nova phases, phase map, deliverable phase, room mapping
 */

import type { JsonValue } from "~/lib/shared/json";

import { FREE_NOVA_AGENTS, isFreeNovaAgent } from "./agents";
import type { NovaAgentType, NovaDeliverableType } from "./types";

export type NovaPhase = "diagnose" | "plan" | "build" | "grow";

/**
 * The phases that stay free under subscription enforcement. Derived from
 * `FREE_NOVA_AGENTS` (the agent-level source of truth) rather than hardcoded:
 * a phase is free exactly when the "diagnose" agent — Nova's free front door —
 * leads it. If a deliverable type / agent later relocates to Plan, the paid
 * line moves automatically because it is tied to the same agent constant.
 */
const FREE_NOVA_PHASES: readonly NovaPhase[] = ["diagnose"].filter(
  (phase): phase is NovaPhase =>
    (FREE_NOVA_AGENTS as readonly string[]).includes(phase),
);

/** True only for the free "diagnose" room; every other room is walled. */
export function isFreeNovaPhase(phase: NovaPhase): boolean {
  return FREE_NOVA_PHASES.includes(phase);
}

/**
 * The deliverables each foundation phase owns. It sets each type's default
 * phase, which the access rules read through `resolveDeliverablePhase`.
 */
export const FOUNDATION_DELIVERABLES = {
  // The 6-Figure Readiness Score lives in its own table, never in a
  // deliverable. A Business Map drawn before map generation was retired still
  // shows here.
  diagnose: ["voice_profile", "business_map"],
  // `core_offer` holds the hero slot ([0]): it is the spine's terminal Plan
  // artifact. Plan COMPLETION never reads this ordering — it is the
  // `isPlanComplete` predicate (`~/lib/shared/nova/plan-completion`).
  // `icp_definition` relocated here from Diagnose with the plan spine (U4) —
  // this listing is what moves the type's DEFAULT phase (and with it the
  // paid line) to Plan. Per-row rendering and access still follow the origin
  // marker (`resolveDeliverablePhase` below): a free-origin ICP keeps living
  // under Diagnose.
  // `identity_statement` (Plan step 2 — Identity Architect) and
  // `ambition_level` (captured post-reveal in Diagnose, or as a Plan
  // fallback) join Plan's static default here. `ambition_level`'s free-origin
  // rows still resolve to Diagnose per-row — see the origin-aware override in
  // `resolveDeliverablePhase` below, the same rule `icp_definition` gets.
  // `mini_sprint` is deliberately NOT listed here even though it's created
  // downstream of Plan work: it's a Build-phase skill output, reviewed on its
  // own asset page.
  plan: [
    "core_offer",
    "icp_definition",
    "identity_statement",
    "ambition_level",
  ],
  // `ninety_day_plan` is the Grow room's hero (and only) deliverable (U6).
  grow: ["ninety_day_plan"],
} as const satisfies Record<string, readonly string[]>;

/**
 * novaDeliverables.type → phase, derived from `FOUNDATION_DELIVERABLES`. Types
 * not listed here have no foundation phase.
 */
export const DELIVERABLE_TYPE_TO_PHASE: Record<string, NovaPhase> =
  Object.fromEntries(
    Object.entries(FOUNDATION_DELIVERABLES).flatMap(([phase, types]) =>
      types.map((type) => [type, phase as NovaPhase]),
    ),
  );

// ---------------------------------------------------------------------------
// Code-stamped deliverables — written by code, never reviewed
// ---------------------------------------------------------------------------

/**
 * Deliverable types written by CODE, never by the model. Each has a dedicated
 * stamp path that upserts the row DIRECTLY — `stampAmbitionLevelDeliverable`
 * (the `save_ambition` tool's helper) — so the row never gets a review-queue
 * item and nothing ever stamps its `confirmedVersion`.
 *
 * That single fact has two readers, and neither may spell the list out again:
 * `create_deliverable` subtracts these from its default model-facing enum so a
 * payload naming one is refused before the model can fabricate an ambition it
 * was never told; and the asset catalog reads a row of one of these types as
 * APPROVED on sight, because the coach stated it through validated input and
 * there is nothing left to review.
 *
 * Lives here, beside the other per-type facts, rather than in
 * `tools/create-deliverable.ts`: that module pulls in the AI-SDK tool machinery
 * and every deliverable schema, which must not reach the client-safe catalog.
 *
 * Aliases: code-stamped deliverables, non-model-writable deliverables,
 * reviewless deliverables
 */
export const CODE_STAMPED_DELIVERABLE_TYPES = [
  "ambition_level",
] as const satisfies readonly NovaDeliverableType[];

const CODE_STAMPED_TYPE_SET: ReadonlySet<string> = new Set(
  CODE_STAMPED_DELIVERABLE_TYPES,
);

/** True when the type is code-stamped, and therefore never reviewed. */
export function isCodeStampedDeliverableType(type: string): boolean {
  return CODE_STAMPED_TYPE_SET.has(type);
}

// ---------------------------------------------------------------------------
// Deliverable origin marker — the R13 grandfather (stamped, not joined)
// ---------------------------------------------------------------------------

/**
 * JSONB payload key carrying a deliverable's origin access class. Server-owned:
 * stamped by `persistDeliverableWithQueueItem` at creation from the creating
 * agent, never accepted from a model payload (the type schemas strip unknown
 * keys and the persist path overwrites the key unconditionally) and filtered
 * out of model-facing reads.
 *
 * Aliases: deliverable origin, origin marker, free-era grandfather
 */
export const DELIVERABLE_ORIGIN_KEY = "origin";

/**
 * The access class a deliverable was created under: `free` when the creating
 * agent is free-tier (diagnose), `paid` otherwise. Chosen
 * over storing the agent name so access checks read one binary value with no
 * agent→class mapping at read time.
 */
export type DeliverableOrigin = "free" | "paid";

/** The origin class the given creating agent stamps onto its deliverables. */
export function deliverableOriginForAgent(
  agentType: NovaAgentType,
): DeliverableOrigin {
  return isFreeNovaAgent(agentType) ? "free" : "paid";
}

/**
 * Read a deliverable row's origin from its JSONB payload. Markerless rows
 * classify as FREE origin: the marker ships with the plan-spine cutover, so a
 * row without one can only predate it — and pre-cutover ICPs were made on the
 * free tier. Deliberately data-only (never a `conversationId` join): the FK is
 * nullable and set-null on conversation delete, so a coach tidying old chats
 * must not lose free access to their own free-era work.
 */
export function readDeliverableOrigin(
  data: Record<string, JsonValue> | null | undefined,
): DeliverableOrigin {
  return data?.[DELIVERABLE_ORIGIN_KEY] === "paid" ? "paid" : "free";
}

/**
 * Deliverable types whose static Plan-default phase yields to a free-origin
 * row's actual creating room. `icp_definition` predates this list (the
 * original R13 grandfather); `ambition_level` joins it because it can be
 * captured in EITHER room — post-reveal in the free Diagnose flow, or as a
 * fallback in the paid Plan Identity Architect step — so a free-origin
 * ambition, like a free-origin ICP, must render under Diagnose rather than
 * hide behind the Plan room lock.
 */
const ORIGIN_AWARE_TYPES: ReadonlySet<string> = new Set([
  "icp_definition",
  "ambition_level",
]);

/**
 * The phase (room) a deliverable ROW belongs to, origin-aware. For the
 * relocated `icp_definition` and `ambition_level` the row's origin decides: a
 * free-origin row (diagnose/strategy-era, or markerless pre-cutover) renders —
 * and stays accessible — under Diagnose; a plan-origin one under Plan. Every
 * other type follows the static `DELIVERABLE_TYPE_TO_PHASE` default. This is
 * the single rule Library/room grouping AND the deliverable access check key
 * on, so a free coach's ICP or ambition can never hide behind the Plan room
 * lock.
 *
 * Aliases: resolve deliverable phase, origin-aware room, icp grandfather phase
 */
export function resolveDeliverablePhase(
  type: string,
  data: Record<string, JsonValue> | null | undefined,
): NovaPhase | undefined {
  if (ORIGIN_AWARE_TYPES.has(type) && readDeliverableOrigin(data) === "free") {
    return "diagnose";
  }
  return DELIVERABLE_TYPE_TO_PHASE[type];
}
